import type {
  Price,
  Product as StorefrontProduct,
  Variant,
} from '@scayle/storefront-nuxt'
import type {
  Brand,
  Offer,
  WithContext,
  Product as SchemaProduct,
  ProductGroup,
  Audience,
  Organization,
} from 'schema-dts'
import {
  isInStock,
  getLowestPrice as getScayleLowestPrice,
} from '@scayle/storefront-nuxt'
import { getTotalAppliedReductions } from '#layers/base/utils/price'
import {
  getSizeLabel,
  getSizeParamFromVariant,
} from '#layers/base/types/variant'

export const metaTagGenerator = (content: {
  description?: string
  robots?: string
  canonical?: string
  socialCards?: any
}) => {
  const metaInfo: any = {
    description: content.description,
    robots: content.robots,
    canonical: content.canonical,
    socialCards: content.socialCards,
  }
  metaInfo.link?.forEach(
    (l: any) => l.rel === 'canonical' && (l.hid = 'canonical'),
  )
  metaInfo.meta?.forEach((l: any) => l.name === 'robots' && (l.hid = 'robots'))
  return metaInfo
}

export const generateOrganizationSchema = ({
  organizationDescription,
  organizationName,
  organizationLogo,
  organisationImage,
  organisationParentName,
  organizationParentUrl,
  locale,
}: {
  organizationDescription: string
  organizationName: string
  organisationImage: string
  organizationLogo: string
  organisationParentName: string
  organizationParentUrl: string
  locale?: string
}): WithContext<Organization> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    name: organizationName,
    description: organizationDescription,
    ...(!!organisationImage && { image: organisationImage }),
    logo: organizationLogo,
    sameAs: [
      'https://www.instagram.com/madeleine_fashion/',
      'https://www.facebook.com/madeleine.de',
      'https://www.linkedin.com/company/madeleine-fashion-group/',
    ],
    parentOrganization: {
      '@type': 'OnlineBusiness',
      name: organisationParentName,
      url: organizationParentUrl,
    },
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      '@id': '#MerchantReturnPolicy',
      applicableCountry: (locale ?? 'DE').toUpperCase(),
      returnPolicyCategory:
        'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    },
  }
}

export const generateProductGroupSchema = ({
  product,
  price,
  productMasterKey,
  productName,
  brandName,
  color,
  description,
  material,
  url,
  baseProductUrlForVariants,
  images = [],
  siblings,
  variants,
  priceCurrency = 'EUR',
  organizationName,
  locale: _locale,
}: {
  product: StorefrontProduct
  productName: string
  brandName: string
  url: string
  baseProductUrlForVariants?: string
  color: string
  organizationName: string
  locale: string
  variants: Variant[]
  price?: Price
  siblings?: StorefrontProduct[]
  description?: string
  material?: string
  images?: string[]
  productMasterKey?: string
  priceCurrency?: string
}): WithContext<ProductGroup> => {
  const brand: Brand = {
    '@type': 'Brand',
    name: brandName,
  }

  const audience: Audience = {
    '@type': 'PeopleAudience',
    suggestedGender: 'female',
    suggestedMinAge: 13,
  }

  const manufacturer: Organization = {
    '@type': 'Organization',
    name: organizationName,
  }

  // `url` is the canonical product URL and already ends with the current
  // product's id (e.g. `/p/<slug>/25882`). For sibling color variants we
  // swap the trailing id instead of appending, otherwise we produce broken
  // nested URLs like `/p/<slug>/25882/25878` that 404 when crawled.
  const buildVariantUrl = (variantProductId: number) =>
    url.replace(/\/\d+(?=\/?$)/, `/${variantProductId}`)

  const colorVariants = [
    generateProductSchema({
      price: price ?? getScayleLowestPrice(variants),
      isInStock: !product.isSoldOut,
      productName: `${productName} - ${color}`,
      brandName,
      color,
      sku: product.referenceKey,
      images: images,
      priceCurrency,
      includeContext: false,
      url: buildVariantUrl(product.id),
    }),
    ...(siblings?.map((sibling) => ({
      url: buildVariantUrl(sibling.id),
    })) || []),
  ]

  const sizeVariants = variants.map((variant) => {
    const sizeParam = getSizeParamFromVariant(variant)
    const variantUrl =
      baseProductUrlForVariants && sizeParam
        ? `${baseProductUrlForVariants}?size=${encodeURIComponent(sizeParam)}`
        : undefined

    return generateProductSchema({
      price: variant.price,
      isInStock: isInStock(variant),
      productName: `${productName} - ${getSizeLabel(variant)}`,
      brandName,
      color,
      size: getSuffix(variant.referenceKey),
      sku: variant.referenceKey,
      url: variantUrl,
      images: images[0],
      priceCurrency,
      includeContext: false,
    })
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    name: productName,
    description,
    material,
    mpn: productMasterKey,
    audience,
    brand,
    manufacturer,
    productGroupID: productMasterKey,
    url,
    variesBy: ['https://schema.org/size', 'https://schema.org/color'],
    hasVariant: [...sizeVariants, ...colorVariants],
  } as WithContext<ProductGroup>
}

export const generateProductSchema = ({
  price,
  productName,
  brandName,
  color,
  size,
  sku,
  url,
  isInStock = false,
  images = [],
  priceCurrency,
  includeContext = true,
}: {
  productName: string
  color: string
  brandName: string
  price?: Price
  sku?: string
  url?: string
  size?: string
  isInStock?: boolean
  images?: string[] | string
  priceCurrency?: string
  includeContext?: boolean
}): WithContext<SchemaProduct> => {
  const offers = generateSchemaProductOffers({
    price,
    isInStock,
    priceCurrency,
  })

  const brand: Brand = {
    '@type': 'Brand',
    name: brandName,
  }

  const product: any = {
    ...(includeContext && { '@context': 'https://schema.org' }),
    '@type': 'Product',
    name: productName,
    color,
    size: size ? size : 'N/A',
    sku,
    url,
    ...(images.length && { image: images }),
    brand,
    offers: {
      ...offers,
    },
  }

  if (!includeContext) {
    product.offers.shippingDetails = { '@id': '#shipping_policy' }
    product.offers.hasMerchantReturnPolicy = { '@id': '#MerchantReturnPolicy' }
  }

  return product as WithContext<SchemaProduct>
}

const generateSchemaProductOffers = ({
  price,
  isInStock = false,
  priceCurrency = 'EUR',
}: {
  isInStock: boolean
  price?: Price
  priceCurrency?: string
}): Offer => {
  const offer: Offer = {
    '@type': 'Offer',
    priceCurrency,
    availability: isInStock
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
    ...(isInStock && { itemCondition: 'https://schema.org/NewCondition' }),
  }

  if (price) {
    const priceWithTax = (price.withTax / 100).toFixed(2)
    const appliedReductions = getTotalAppliedReductions(
      price,
      price.appliedReductions,
    )
    const originalPrice = (
      (price.withTax + appliedReductions.absoluteWithTax) /
      100
    ).toFixed(2)

    offer.price = priceWithTax

    const hasReduction =
      appliedReductions.absoluteWithTax > 0 &&
      Number(originalPrice) > Number(priceWithTax)

    offer.priceSpecification = hasReduction
      ? {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/StrikethroughPrice',
          price: originalPrice,
          priceCurrency,
        }
      : {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/ListPrice',
          price: priceWithTax,
          priceCurrency,
        }
  }

  return offer
}

export const getSharedShippingDetails = (
  priceCurrency = 'EUR',
  country = 'DE',
) => ({
  '@context': 'https://schema.org/',
  '@type': 'OfferShippingDetails',
  '@id': '#shipping_policy',
  shippingRate: {
    '@type': 'MonetaryAmount',
    value: '5.95',
    currency: priceCurrency,
  },
  shippingDestination: {
    '@type': 'DefinedRegion',
    addressCountry: country,
  },
})

/**
 * Strips off all parameters (except page) from input canonical url string
 * @param url canonical
 * @returns url string
 */
export const sanitizeCanonical = (url: string | undefined): string => {
  if (!url) {
    return ''
  }
  const baseAndParams = url?.split('?')
  const baseUrl = baseAndParams?.[0]
  const parametersPart = baseAndParams?.[1] // part of url after '?'

  if (!parametersPart) {
    // no url parameters exist, so return the original url
    return url
  }
  const parametersAndValues = parametersPart.split('&')

  // For each key=value combination, check for page and filter attribute. Return the combination if found
  return `${baseUrl}?${parametersAndValues
    .filter((attribute) => !attribute.toLocaleLowerCase().includes('page=1'))
    .join('&')}`
}

// Helper function which returns the whole size suffix from the variant reference key
const getSuffix = (refKey?: string) => {
  if (!refKey) {
    return
  }
  const parts = refKey.split('-')
  return `${parts[1]}-${parts[2]}`
}
